import { useState } from 'react';
import {
  AlertBanner,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  ProcessingBasisNote,
  Select,
  StatCard,
  Toast,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints, type PointRuleRow } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';
import { formatDate } from '../lib/format.js';
import { usePeriode } from '../state/periode.js';
import { awalBulan } from '../lib/periode.js';

const SUBJECT_ROLES = ['ACTOR', 'REPORTER', 'OWNER', 'VERIFIER'] as const;
const CAP_PERIODS = ['NONE', 'DAY', 'WEEK', 'MONTH'] as const;

/**
 * POIN & ATURAN — Figma `116:768` · M10 · rute `/points` · izin MGR.
 *
 * EMPAT ATURAN KEADILAN PETA §5.7, DAN DI MANA MASING-MASING TERLIHAT DI LAYAR INI
 * ------------------------------------------------------------------------------
 *  1. *"poin hanya untuk perilaku yang dapat diperiksa"* — daftar peristiwa pemicu datang
 *     dari SERVER (`trigger_events`), dan ia tertutup. Layar tidak dapat menawarkan
 *     peristiwa yang tidak pernah diterbitkan sistem, jadi ia tidak dapat membuat aturan
 *     yang tampak aktif dan tidak pernah memberi poin.
 *  2. *"batas mingguan mencegah satu orang membanjiri sistem"* — `cap_period` dan
 *     `cap_value` adalah kolom aturan, dan formulirnya menolak periode tanpa nilainya.
 *  3. *"setiap pemberian masuk jejak audit"* — kolom "Disetujui oleh" pada buku besar
 *     bukan hiasan: ia yang membuat sebuah pemberian dapat dijelaskan kepada orang yang
 *     TIDAK menerimanya.
 *  4. *"poin yang sudah final tidak ditarik saat modul dimatikan"* — tidak ada satu pun
 *     kendali di layar ini yang dapat menariknya. Membatalkan pemberian adalah operasi
 *     tersendiri yang MENUNTUT ALASAN, dan barisnya tetap ada sesudahnya.
 *
 * SATU HAL YANG LAYAR INI TIDAK PUNYA, DAN ITU DISENGAJA: tombol "beri poin". Poin lahir
 * dari peristiwa, bukan dari tombol — seorang manajer yang dapat memberi poin langsung
 * adalah seluruh alasan sebuah papan peringkat berhenti dipercaya.
 */
export function PointsRules() {
  const { t, locale, translator } = useSession();
  /** Periode dari bilah atas (`5:58`). */
  const { periode } = usePeriode();
  const data = useResource(
    async () => {
      const [rules, ledger, leaderboard] = await Promise.all([
        endpoints.pointRules(),
        endpoints.pointLedger(),
        endpoints.leaderboard(awalBulan(periode)),
      ]);
      return { rules, ledger, leaderboard };
    },
    [periode],
  );

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: '',
    trigger_event: '',
    subject_role: 'ACTOR',
    points: '5',
    award_state: 'PROVISIONAL',
    cap_period: 'WEEK',
    cap_value: '50',
  });
  const [pausing, setPausing] = useState<PointRuleRow | null>(null);
  const [reversing, setReversing] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function buatAturan() {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.createPointRule({
        code: form.code.trim(),
        trigger_event: form.trigger_event,
        subject_role: form.subject_role,
        points: Number(form.points),
        award_state: form.award_state,
        cap_period: form.cap_period,
        cap_value: form.cap_period === 'NONE' ? null : Number(form.cap_value),
      });
      setToast(t('points.rule.created', { code: form.code.trim() }));
      setCreating(false);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function ubahKeadaan(rule: PointRuleRow) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.patchPointRule(rule.code, {
        state: rule.state === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
      });
      setToast(t('points.rule.changed', { code: rule.code }));
      setPausing(null);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function setujui(id: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.approvePoint(id);
      setToast(t('points.award.approved'));
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function batalkan(id: string, alasan: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.reversePoint(id, alasan);
      setToast(t('points.award.reversed'));
      setReversing(null);
      setReason('');
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      title={t('points.title')}
      subtitle={t('points.subtitle')}
      resource={data}
      moduleCode="M10"
      actions={
        <Button variant="primary" onClick={() => setCreating(true)}>
          {t('points.rule.add')}
        </Button>
      }
    >
      {(loaded) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          <div className="stat-grid">
            <StatCard
              label={t('points.stat.activeRules')}
              value={String(loaded.rules.items.filter((r) => r.state === 'ACTIVE').length)}
            />
            <StatCard
              label={t('points.stat.pending')}
              value={String(loaded.ledger.page_totals.pending_approval)}
              tone={loaded.ledger.page_totals.pending_approval > 0 ? 'warning' : 'neutral'}
              caption={t('points.stat.pending.caption')}
            />
            <StatCard
              label={t('points.stat.finalOnPage')}
              value={String(loaded.ledger.page_totals.final_points)}
              /*
                Keterangan MENYEBUTKAN bahwa angkanya dari halaman ini, bukan dari seluruh
                buku besar. Sebuah "total" di atas tabel berisi 50 baris yang sesungguhnya
                menghitung ribuan baris adalah angka yang tidak dapat diperiksa siapa pun.
              */
              caption={t('points.stat.finalOnPage.caption', {
                rows: loaded.ledger.page_totals.rows,
              })}
            />
            <StatCard
              label={t('points.stat.topRank')}
              value={loaded.leaderboard.rows[0]?.user.full_name ?? '—'}
              caption={
                loaded.leaderboard.rows[0]
                  ? t('points.stat.topRank.caption', {
                      points: loaded.leaderboard.rows[0].final_points,
                    })
                  : undefined
              }
            />
          </div>

          <section className="card">
            <h2 className="card__title">{t('points.rules')}</h2>
            {loaded.rules.items.length === 0 ? (
              <EmptyState
                title={t('points.rules.empty.title')}
                body={t('points.rules.empty.body')}
                action={{ label: t('points.rule.add'), onClick: () => setCreating(true) }}
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('points.column.rule')}</th>
                    <th scope="col">{t('points.column.trigger')}</th>
                    <th scope="col">{t('points.column.subject')}</th>
                    <th scope="col">{t('points.column.points')}</th>
                    <th scope="col">{t('points.column.cap')}</th>
                    <th scope="col">{t('points.column.state')}</th>
                    <th scope="col">{t('locations.column.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.rules.items.map((rule) => (
                    <tr key={rule.code}>
                      <td>{rule.code}</td>
                      <td>{t(`points.trigger.${rule.trigger_event}`)}</td>
                      <td>{t(`points.subject.${rule.subject_role}`)}</td>
                      <td>
                        {rule.points}
                        <br />
                        <span className="card__muted">
                          {t(`points.awardState.${rule.award_state}`)}
                        </span>
                      </td>
                      <td>
                        {rule.cap_period === 'NONE'
                          ? t('points.cap.none')
                          : t('points.cap.value', {
                              value: rule.cap_value ?? 0,
                              period: t(`points.capPeriod.${rule.cap_period}`),
                            })}
                      </td>
                      <td>
                        <Badge badgeTone={rule.state === 'ACTIVE' ? 'SUCCESS' : 'NEUTRAL'}>
                          {t(`points.ruleState.${rule.state}`)}
                        </Badge>
                      </td>
                      <td>
                        <Button variant="text" onClick={() => setPausing(rule)}>
                          {rule.state === 'ACTIVE' ? t('points.rule.pause') : t('points.rule.resume')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {loaded.rules.processing_basis ? (
              <ProcessingBasisNote
                label={t('privacy.basisNote.label')}
                sentence={t('privacy.basisNote.sentence', {
                  basis: t(`privacy.basis.${loaded.rules.processing_basis.legal_basis}`),
                })}
                article={loaded.rules.processing_basis.article}
                purposes={t('privacy.basisNote.purposes', {
                  purposes: loaded.rules.processing_basis.purposes
                    .map((p) => t(`privacy.purpose.${p}`))
                    .join(', '),
                })}
                moreLabel={t('privacy.basisNote.more')}
              />
            ) : null}
          </section>

          <section className="card">
            <h2 className="card__title">{t('points.ledger')}</h2>
            {loaded.ledger.items.length === 0 ? (
              <p className="card__muted">{t('points.ledger.empty')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('points.column.person')}</th>
                    <th scope="col">{t('points.column.rule')}</th>
                    <th scope="col">{t('points.column.points')}</th>
                    <th scope="col">{t('points.column.state')}</th>
                    <th scope="col">{t('points.column.approval')}</th>
                    <th scope="col">{t('points.column.awardedAt')}</th>
                    <th scope="col">{t('locations.column.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.ledger.items.map((row) => (
                    <tr key={row.id}>
                      <td>{row.user.full_name ?? '—'}</td>
                      <td>{row.rule_code}</td>
                      <td>{row.points}</td>
                      <td>
                        <Badge
                          badgeTone={
                            row.state === 'FINAL'
                              ? 'SUCCESS'
                              : row.state === 'REVERSED'
                                ? 'NEUTRAL'
                                : 'INFORMATION'
                          }
                        >
                          {t(`points.ledgerState.${row.state}`)}
                        </Badge>
                      </td>
                      <td>{t(`points.approval.${row.approval_state}`)}</td>
                      <td>{formatDate(row.awarded_at, locale)}</td>
                      <td className="page__actions">
                        {row.approval_state === 'PENDING' ? (
                          <Button variant="primary" busy={busy} onClick={() => void setujui(row.id)}>
                            {t('points.award.approve')}
                          </Button>
                        ) : null}
                        {row.state !== 'REVERSED' ? (
                          <Button variant="text" onClick={() => setReversing(row.id)}>
                            {t('points.award.reverse')}
                          </Button>
                        ) : (
                          <span className="card__muted">{row.reversal_reason ?? '—'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="card">
            <h2 className="card__title">
              {t('points.leaderboard', { month: loaded.leaderboard.period_month })}
            </h2>
            {loaded.leaderboard.rows.length === 0 ? (
              <p className="card__muted">{t('points.leaderboard.empty')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('points.column.rank')}</th>
                    <th scope="col">{t('points.column.person')}</th>
                    <th scope="col">{t('points.column.final')}</th>
                    <th scope="col">{t('points.column.provisional')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.leaderboard.rows.map((row) => (
                    <tr key={row.user.id}>
                      <td>{row.rank}</td>
                      <td>{row.user.full_name ?? '—'}</td>
                      <td>{row.final_points}</td>
                      <td>{row.provisional_points ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {/* Aturan keadilan yang paling mudah disalahpahami, dinyatakan di layar. */}
            <p className="card__muted">{t('points.leaderboard.finalOnly')}</p>
          </section>

          <Dialog
            open={creating}
            kind="confirm"
            title={t('points.rule.add')}
            description={t('points.rule.add.describe')}
            confirmLabel={t('app.save')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            confirmDisabled={form.code.trim().length < 3 || form.trigger_event === ''}
            onConfirm={() => void buatAturan()}
            onCancel={() => setCreating(false)}
          >
            <Input
              label={t('points.column.rule')}
              value={form.code}
              maxLength={40}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              hint={t('points.rule.codeHint')}
            />
            <Select
              label={t('points.column.trigger')}
              value={form.trigger_event}
              onChange={(e) => setForm({ ...form, trigger_event: e.target.value })}
              options={[
                { value: '', label: t('report.field.choose') },
                ...loaded.rules.trigger_events.map((ev) => ({
                  value: ev,
                  label: t(`points.trigger.${ev}`),
                })),
              ]}
            />
            <Select
              label={t('points.column.subject')}
              value={form.subject_role}
              onChange={(e) => setForm({ ...form, subject_role: e.target.value })}
              options={SUBJECT_ROLES.map((r) => ({ value: r, label: t(`points.subject.${r}`) }))}
            />
            <Input
              label={t('points.column.points')}
              inputType="number"
              min={1}
              max={1000}
              value={form.points}
              onChange={(e) => setForm({ ...form, points: e.target.value })}
            />
            <Select
              label={t('points.column.cap')}
              value={form.cap_period}
              onChange={(e) => setForm({ ...form, cap_period: e.target.value })}
              options={CAP_PERIODS.map((p) => ({ value: p, label: t(`points.capPeriod.${p}`) }))}
            />
            {form.cap_period !== 'NONE' ? (
              <Input
                label={t('points.rule.capValue')}
                inputType="number"
                min={1}
                value={form.cap_value}
                onChange={(e) => setForm({ ...form, cap_value: e.target.value })}
              />
            ) : null}
          </Dialog>

          <Dialog
            open={pausing !== null}
            kind="confirm"
            title={pausing?.state === 'ACTIVE' ? t('points.rule.pause') : t('points.rule.resume')}
            description={t('points.rule.pause.describe', { code: pausing?.code ?? '' })}
            confirmLabel={t('app.save')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            onConfirm={() => {
              if (pausing) void ubahKeadaan(pausing);
            }}
            onCancel={() => setPausing(null)}
          />

          {/*
            Membatalkan pemberian MENUNTUT ALASAN, dan dialognya `destructive`: poin yang
            hilang dari papan peringkat seseorang tanpa penjelasan adalah cara paling cepat
            membuat seluruh papan itu berhenti dipercaya.
          */}
          <Dialog
            open={reversing !== null}
            kind="destructive"
            title={t('points.award.reverse')}
            description={t('points.award.reverse.describe')}
            confirmLabel={t('points.award.reverse')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            confirmDisabled={reason.trim().length < 3}
            onConfirm={() => {
              if (reversing) void batalkan(reversing, reason.trim());
            }}
            onCancel={() => {
              setReversing(null);
              setReason('');
            }}
          >
            <Input
              label={t('finding.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={reason.trim().length < 3 ? t('error.REASON_REQUIRED') : null}
            />
          </Dialog>

          {toast ? (
            <div className="assist-toast-stack">
              <Toast
                kind="success"
                signalWord={t('signal.success')}
                title={toast}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            </div>
          ) : null}
        </>
      )}
    </Screen>
  );
}
